/**
 * Quick Deal Analyzer Page
 * Fast deal analysis without creating a full deal record
 */

import React, { useState, useCallback } from 'react';
import {
  Calculator,
  ArrowRight,
  TrendUp,
  CurrencyEur,
  Sparkle,
} from '@phosphor-icons/react';
import { calculatorApi } from '../utils/api';
import {
  formatCurrency,
  formatPercent,
  formatPricePerSqm,
  getRoiColorClass,
  getProfitColorClass,
} from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { cn } from '../lib/utils';

export const QuickAnalyzer = () => {
  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState({
    purchase_price: 500000,
    surface_sqm: 60,
    renovation_budget: 50000,
    exit_price: 700000,
    notary_percent: 7.5,
    contingency_percent: 10,
    sales_fee_percent: 3,
    target_roi_percent: 20,
  });
  const [results, setResults] = useState(null);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleCalculate = useCallback(async () => {
    try {
      setLoading(true);
      const data = await calculatorApi.quickAnalysis(inputs);
      setResults(data.results);
    } catch (err) {
      console.error('Calculation failed:', err);
    } finally {
      setLoading(false);
    }
  }, [inputs]);

  const formatInput = (value) => {
    return new Intl.NumberFormat('de-DE').format(value);
  };

  const parseInput = (str) => {
    return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
  };

  return (
    <div data-testid="quick-analyzer" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="card-premium bg-gradient-to-r from-[#3A4A40]/5 to-transparent">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#3A4A40] flex items-center justify-center">
            <Calculator size={24} className="text-white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Quick Deal Analyzer</h2>
            <p className="text-muted-foreground">
              Fast analysis without creating a full deal record
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="card-premium">
          <h3 className="font-semibold mb-6">Deal Parameters</h3>
          
          <div className="space-y-6">
            {/* Core Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="purchase_price">Purchase Price (€)</Label>
                <Input
                  id="purchase_price"
                  data-testid="input-purchase-price"
                  type="text"
                  value={formatInput(inputs.purchase_price)}
                  onChange={(e) => handleInputChange('purchase_price', parseInput(e.target.value))}
                  className="font-tabular"
                />
              </div>
              <div>
                <Label htmlFor="surface_sqm">Surface (m²)</Label>
                <Input
                  id="surface_sqm"
                  data-testid="input-surface"
                  type="number"
                  value={inputs.surface_sqm}
                  onChange={(e) => handleInputChange('surface_sqm', e.target.value)}
                  className="font-tabular"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="renovation_budget">Renovation Budget (€)</Label>
                <Input
                  id="renovation_budget"
                  data-testid="input-renovation"
                  type="text"
                  value={formatInput(inputs.renovation_budget)}
                  onChange={(e) => handleInputChange('renovation_budget', parseInput(e.target.value))}
                  className="font-tabular"
                />
              </div>
              <div>
                <Label htmlFor="exit_price">Target Exit Price (€)</Label>
                <Input
                  id="exit_price"
                  data-testid="input-exit-price"
                  type="text"
                  value={formatInput(inputs.exit_price)}
                  onChange={(e) => handleInputChange('exit_price', parseInput(e.target.value))}
                  className="font-tabular"
                />
              </div>
            </div>

            {/* Sliders for percentages */}
            <div className="space-y-4 pt-4 border-t">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Notary Costs</Label>
                  <span className="text-sm font-tabular">{inputs.notary_percent}%</span>
                </div>
                <Slider
                  data-testid="slider-notary"
                  value={[inputs.notary_percent]}
                  onValueChange={([val]) => handleInputChange('notary_percent', val)}
                  min={5}
                  max={10}
                  step={0.5}
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Renovation Contingency</Label>
                  <span className="text-sm font-tabular">{inputs.contingency_percent}%</span>
                </div>
                <Slider
                  data-testid="slider-contingency"
                  value={[inputs.contingency_percent]}
                  onValueChange={([val]) => handleInputChange('contingency_percent', val)}
                  min={5}
                  max={25}
                  step={1}
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Sales Fee</Label>
                  <span className="text-sm font-tabular">{inputs.sales_fee_percent}%</span>
                </div>
                <Slider
                  data-testid="slider-sales-fee"
                  value={[inputs.sales_fee_percent]}
                  onValueChange={([val]) => handleInputChange('sales_fee_percent', val)}
                  min={1}
                  max={5}
                  step={0.5}
                />
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Target ROI</Label>
                  <span className="text-sm font-tabular">{inputs.target_roi_percent}%</span>
                </div>
                <Slider
                  data-testid="slider-target-roi"
                  value={[inputs.target_roi_percent]}
                  onValueChange={([val]) => handleInputChange('target_roi_percent', val)}
                  min={10}
                  max={40}
                  step={1}
                />
              </div>
            </div>

            <Button 
              data-testid="calculate-btn"
              onClick={handleCalculate}
              disabled={loading}
              className="w-full bg-[#3A4A40] hover:bg-[#2E3B33]"
            >
              {loading ? (
                'Calculating...'
              ) : (
                <>
                  <Sparkle size={18} className="mr-2" />
                  Calculate Deal
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {results ? (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="card-premium text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Expected ROI
                  </p>
                  <p className={cn(
                    'text-3xl font-bold font-tabular',
                    getRoiColorClass(results.roi_percent)
                  )}>
                    {formatPercent(results.roi_percent)}
                  </p>
                </div>
                <div className="card-premium text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Gross Profit
                  </p>
                  <p className={cn(
                    'text-3xl font-bold font-tabular',
                    getProfitColorClass(results.gross_profit)
                  )}>
                    {formatCurrency(results.gross_profit)}
                  </p>
                </div>
              </div>

              {/* Cost Breakdown */}
              <div className="card-premium">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <CurrencyEur size={18} />
                  Cost Analysis
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Acquisition</span>
                    <span className="font-tabular">{formatCurrency(results.total_acquisition_cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground pl-4">Per m²</span>
                    <span className="font-tabular">{formatPricePerSqm(results.acquisition_per_sqm)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Renovation</span>
                    <span className="font-tabular">{formatCurrency(results.total_renovation)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground pl-4">Per m²</span>
                    <span className="font-tabular">{formatPricePerSqm(results.renovation_per_sqm)}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between font-semibold">
                    <span>Total Project Cost</span>
                    <span className="font-tabular">{formatCurrency(results.total_project_cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground pl-4">Per m²</span>
                    <span className="font-tabular">{formatPricePerSqm(results.total_cost_per_sqm)}</span>
                  </div>
                </div>
              </div>

              {/* Profit Analysis */}
              <div className="card-premium">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendUp size={18} />
                  Profit Analysis
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Profit</span>
                    <span className={cn('font-tabular font-medium', getProfitColorClass(results.gross_profit))}>
                      {formatCurrency(results.gross_profit)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Margin</span>
                    <span className="font-tabular">{formatPercent(results.gross_margin_percent)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ROI</span>
                    <span className={cn('font-tabular font-medium', getRoiColorClass(results.roi_percent))}>
                      {formatPercent(results.roi_percent)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Decision Support */}
              <div className="card-premium border-dashed">
                <h4 className="font-semibold mb-4">Decision Support</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Break-even Exit Price</span>
                    <span className="font-tabular">{formatCurrency(results.break_even_exit_price)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground pl-4">Per m²</span>
                    <span className="font-tabular">{formatPricePerSqm(results.break_even_per_sqm)}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Max Bid @ {inputs.target_roi_percent}% ROI</span>
                    <span className="font-tabular font-medium text-primary">
                      {formatCurrency(results.max_bid_at_target_roi)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground pl-4">Per m²</span>
                    <span className="font-tabular">{formatPricePerSqm(results.max_bid_per_sqm)}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="card-premium flex flex-col items-center justify-center min-h-[400px] text-center">
              <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                <Calculator size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Enter Deal Parameters</h3>
              <p className="text-muted-foreground max-w-sm">
                Fill in the deal parameters on the left and click "Calculate Deal" to see the analysis results.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickAnalyzer;
